import { useState } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
} from '@heroui/react';
import { Camera, FileUp, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { useUploadKnowledge, useCreateLink } from '@/api/knowledge';

interface MobileUploadSheetProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  folderId?: string;
}

export default function MobileUploadSheet({
  isOpen,
  onClose,
  projectId,
  folderId,
}: MobileUploadSheetProps) {
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [linkTitle, setLinkTitle] = useState('');
  const [linkUrl, setLinkUrl] = useState('');

  const uploadMutation = useUploadKnowledge(projectId);
  const createLinkMutation = useCreateLink(projectId);

  const resetForm = () => {
    setShowLinkForm(false);
    setLinkTitle('');
    setLinkUrl('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleFileSelected = (file: File) => {
    const formData = new FormData();
    formData.append('files', file);
    formData.append('type', 'DRAWING');
    if (folderId) formData.append('folder_id', folderId);

    toast.promise(
      new Promise((resolve, reject) => {
        uploadMutation.mutate(formData, {
          onSuccess: resolve,
          onError: reject,
        });
      }),
      {
        loading: `Uploading ${file.name}...`,
        success: `${file.name} uploaded`,
        error: (err) => `Upload failed: ${err?.message ?? 'Unknown error'}`,
      },
    );

    handleClose();
  };

  const handleLinkSubmit = () => {
    if (!linkTitle.trim() || !linkUrl.trim()) return;
    createLinkMutation.mutate(
      { title: linkTitle.trim(), url: linkUrl.trim() },
      {
        onSuccess: () => toast.success('Link added'),
        onError: (err) => toast.error(`Failed: ${err.message}`),
      },
    );
    handleClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      placement="bottom"
      size="full"
      classNames={{
        base: 'rounded-t-xl m-0 sm:m-0',
      }}
    >
      <ModalContent style={{ backgroundColor: 'var(--bg-surface)' }}>
        <ModalHeader
          className="text-sm font-semibold"
          style={{ color: 'var(--text-primary)' }}
        >
          Upload
        </ModalHeader>

        <ModalBody className="gap-3">
          {!showLinkForm ? (
            <>
              {/* Take Photo */}
              <label className="block">
                <div
                  className="flex items-center gap-4 p-4 rounded-xl cursor-pointer transition-colors hover:bg-[var(--bg-hover)]"
                  style={{ border: '1px solid var(--border)' }}
                >
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: 'var(--accent)', opacity: 0.9 }}
                  >
                    <Camera size={22} className="text-white" />
                  </div>
                  <div>
                    <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                      Take Photo
                    </div>
                    <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                      Use camera to capture a drawing
                    </div>
                  </div>
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  capture="environment"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleFileSelected(f);
                  }}
                />
              </label>

              {/* Choose File */}
              <label className="block">
                <div
                  className="flex items-center gap-4 p-4 rounded-xl cursor-pointer transition-colors hover:bg-[var(--bg-hover)]"
                  style={{ border: '1px solid var(--border)' }}
                >
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: '#28a745' }}
                  >
                    <FileUp size={22} className="text-white" />
                  </div>
                  <div>
                    <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                      Choose File
                    </div>
                    <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                      PDF, JPEG, PNG, or WebP
                    </div>
                  </div>
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleFileSelected(f);
                  }}
                />
              </label>

              {/* Add Link */}
              <button
                className="flex items-center gap-4 p-4 rounded-xl w-full text-left transition-colors hover:bg-[var(--bg-hover)]"
                style={{ border: '1px solid var(--border)' }}
                onClick={() => setShowLinkForm(true)}
              >
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: '#6366f1' }}
                >
                  <Link2 size={22} className="text-white" />
                </div>
                <div>
                  <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                    Add Link
                  </div>
                  <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    Save a URL reference
                  </div>
                </div>
              </button>
            </>
          ) : (
            /* Link form */
            <>
              <Input
                label="Title"
                size="sm"
                value={linkTitle}
                onValueChange={setLinkTitle}
                placeholder="e.g. Project Specifications"
                classNames={{
                  inputWrapper: 'bg-[var(--bg-panel)] border-[var(--border)]',
                  input: 'text-[var(--text-primary)]',
                  label: 'text-[var(--text-secondary)]',
                }}
              />
              <Input
                label="URL"
                size="sm"
                value={linkUrl}
                onValueChange={setLinkUrl}
                placeholder="https://..."
                type="url"
                classNames={{
                  inputWrapper: 'bg-[var(--bg-panel)] border-[var(--border)]',
                  input: 'text-[var(--text-primary)]',
                  label: 'text-[var(--text-secondary)]',
                }}
              />
            </>
          )}
        </ModalBody>

        <ModalFooter>
          {showLinkForm ? (
            <>
              <Button
                size="sm"
                variant="bordered"
                onPress={() => setShowLinkForm(false)}
                className="border-[var(--border)] text-[var(--text-secondary)]"
              >
                Back
              </Button>
              <Button
                size="sm"
                color="primary"
                onPress={handleLinkSubmit}
                isDisabled={!linkTitle.trim() || !linkUrl.trim()}
              >
                Add Link
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              variant="bordered"
              onPress={handleClose}
              className="border-[var(--border)] text-[var(--text-secondary)]"
            >
              Cancel
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
